import React, { type PropsWithChildren, useCallback } from 'react';
import {
  type GestureResponderEvent,
  StyleSheet,
  TouchableOpacity,
  View,
  type TouchableOpacityProps,
  type ViewProps,
} from 'react-native';
import Animated, {
  interpolateColor,
  type SharedValue,
  type WithTimingConfig,
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
} from 'react-native-reanimated';

import { theme } from '@garnish/constants';

import { HapticFeedbackStyle, triggerHapticFeedback } from '../../../../utils';
import { Icon, type IconName } from '../../../Icon';
import { NavbarLinkBadge } from '../../../NavbarLinkBadge';
import { BodyText } from '../../../Text';

// ─────────────────────────────────────────────────────────────────────────────

export const NavbarLink = (props: Props) => {
  const {
    accessibilityRole = 'tab',
    accessibilityState,
    activeTabIndexSV,
    children,
    disabled,
    hasBadge = false,
    iconName,
    iconNameActive,
    isActive = false,
    onPress,
    style,
    tabIndex,
    ...restProps
  } = props;

  // ─── Derived ─────────────────────────────────────────────────────────

  const isDisabled = disabled === true;
  const activeIconName = iconNameActive ?? iconName;

  const mergedAccessibilityState = {
    ...accessibilityState,
    disabled: isDisabled,
    selected: isActive,
  };

  // ─── Animated Values ─────────────────────────────────────────────────

  const activeSV = useDerivedValue(() => {
    if (activeTabIndexSV !== undefined && tabIndex !== undefined) {
      return withTiming(activeTabIndexSV.value === tabIndex ? 1 : 0, NAVBAR_LINK_TIMING_CONFIG);
    }

    return withTiming(isActive ? 1 : 0, NAVBAR_LINK_TIMING_CONFIG);
  });

  // ─── Animated Styles ─────────────────────────────────────────────────

  const idleIconAnimatedStyles = useAnimatedStyle(() => {
    return {
      opacity: 1 - activeSV.value,
    };
  });

  const activeIconAnimatedStyles = useAnimatedStyle(() => {
    return {
      opacity: activeSV.value,
    };
  });

  const labelAnimatedStyles = useAnimatedStyle(() => {
    return {
      color: interpolateColor(activeSV.value, [0, 1], [theme.colors.CHARCOAL, theme.colors.KALE]),
    };
  });

  const activeBackgroundAnimatedStyles = useAnimatedStyle(() => {
    return { opacity: activeSV.value };
  });

  // ─── Handlers ───────────────────────────────────────────────────────

  const handlePress = useCallback(
    (event: GestureResponderEvent) => {
      void triggerHapticFeedback(HapticFeedbackStyle.Light);
      onPress?.(event);
    },
    [onPress],
  );

  // ─── Styles ──────────────────────────────────────────────────────────

  const touchableStyles = [styles.touchable, style];
  const idleIconStyles = [styles.idleIcon, idleIconAnimatedStyles];
  const activeIconStyles = [styles.activeIcon, activeIconAnimatedStyles];
  const labelStyles = [styles.label, labelAnimatedStyles];
  const activeBackgroundStyles = [styles.activeBackground, activeBackgroundAnimatedStyles];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TouchableOpacity
      accessibilityRole={accessibilityRole}
      accessibilityState={mergedAccessibilityState}
      activeOpacity={NAVBAR_LINK_ACTIVE_OPACITY}
      disabled={isDisabled}
      onPress={handlePress}
      style={touchableStyles}
      {...restProps}
    >
      <Animated.View pointerEvents="none" style={activeBackgroundStyles} />

      <View>
        <Animated.View pointerEvents="none" style={idleIconStyles}>
          <Icon
            color={theme.colors.CHARCOAL}
            height={NAVBAR_LINK_ICON_SIZE}
            name={iconName}
            width={NAVBAR_LINK_ICON_SIZE}
          />
        </Animated.View>

        <Animated.View pointerEvents="none" style={activeIconStyles}>
          <Icon
            color={theme.colors.KALE}
            height={NAVBAR_LINK_ICON_SIZE}
            name={activeIconName}
            width={NAVBAR_LINK_ICON_SIZE}
          />
        </Animated.View>

        {hasBadge ? <NavbarLinkBadge badgeType="mobile" /> : undefined}
      </View>

      {children ? (
        <AnimatedBodyText
          sizeMatch={['12']}
          style={labelStyles}
          numberOfLines={1}
          maxFontSizeMultiplier={1.2}
        >
          {children}
        </AnimatedBodyText>
      ) : undefined}
    </TouchableOpacity>
  );
};

// ─── Animated Components ─────────────────────────────────────────────────────

const AnimatedBodyText = Animated.createAnimatedComponent(BodyText);

// ─── Constants ───────────────────────────────────────────────────────────────

const NAVBAR_LINK_ICON_SIZE = 24;
const NAVBAR_LINK_ACTIVE_OPACITY = 0.7;
const NAVBAR_LINK_TIMING_CONFIG: WithTimingConfig = { duration: theme.transitions.short };

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  touchable: {
    minWidth: 66,
    height: 48,
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 1000,
    paddingHorizontal: theme.spacing['4'],
    overflow: 'hidden',
  },
  activeBackground: {
    ...StyleSheet.absoluteFill,
    backgroundColor: theme.colors.OPACITY.KALE.LIGHTEST,
  },
  idleIcon: {},
  activeIcon: {
    ...StyleSheet.absoluteFill,
  },
  label: {
    textAlign: 'center',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

export type NavbarLinkProps = PropsWithChildren<
  {
    activeTabIndexSV?: SharedValue<number>;
    disabled?: boolean;
    hasBadge?: boolean;
    iconName: IconName;
    iconNameActive?: IconName;
    isActive?: boolean;
    style?: ViewProps['style'];
    tabIndex?: number;
  } & Omit<TouchableOpacityProps, 'children' | 'disabled' | 'style'>
>;

type Props = NavbarLinkProps;
